import React, { useCallback } from "react";
import { DialogHeader, DialogBody } from "components/common/dialogs/GenericDialog";
import { useLocale } from "lib/hooks/useLocale";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { ItemType } from "lib/types/checkout";
import { ItemCard } from "../CheckoutItems";
import { X } from "react-feather";

interface RemoveItemDialogProps {
  itemName: string;
  setIsOpen: () => void;
  onConfirm: (isLastItem: boolean) => void;
  isCartItem?: boolean;
  itemProps: ItemType;
}

const RemoveItemDialog: React.FC<RemoveItemDialogProps> = React.memo(
  ({ itemName, setIsOpen, onConfirm, isCartItem = false, itemProps }) => {
    const { t } = useLocale();

    const {
      state: { checkoutItems },
    } = useCheckoutContext();

    const isLastItem = useCallback(() => {
      const checkoutItemsLength = checkoutItems?.length;
      if (checkoutItemsLength <= 1) {
        return true;
      }
      let nonFreebieCount = 0;
      checkoutItems.forEach((checkoutItem) => {
        if (!checkoutItem.is_freebie) {
          nonFreebieCount++;
        }
      });
      if (nonFreebieCount === 1) {
        return true;
      }
      return false;
    }, [checkoutItems]);

    return (
      <>
        <DialogHeader>
          <div className="flex h-full w-full flex-row items-center justify-between">
            <h1 className="text-base font-medium text-carbon-dark">
              {t("remove_items", {
                item_count: itemProps?.quantity === 1 ? "1 item" : `${itemProps.quantity} items`,
              })}
            </h1>
            <button className="outline-none">
              <X className="h-6 w-6 cursor-pointer text-coal-dark" onClick={() => setIsOpen()} />
            </button>
          </div>
        </DialogHeader>

        <DialogBody>
          <div className="relative flex w-full flex-col space-y-6 px-6">
            <ItemCard
              {...itemProps}
              checkoutItemsMutable={false}
              disableOOSItems={true}
              isMutable={false}
              isCartItem={false}
            />
            <PrimaryButton
              buttonText={
                Boolean(isLastItem()) ? t("remove_last_item_dialog_cta") : t("remove_item_dialog_cta")
              }
              onClick={() => onConfirm(isLastItem())}
              height="h-14"
              isLoading={false}
              isDisabled={false}
            />
          </div>
        </DialogBody>
      </>
    );
  },
);

export default RemoveItemDialog;
